/**
 * apps/dashboard/src/api/transforms/myWork.ts
 *
 * Pure transform: converts a MyWorkCompositeResult (raw Convex documents)
 * into a MyWorkView (view-model for screen components).
 *
 * Single-argument signature — no `partial` parameter (F-14 closure: a
 * two-argument signature creates a caller-override footgun).
 *
 * No side effects, no Date.now() calls, no module-level mutable state.
 */
import type { Doc } from "../../../../../convex/_generated/dataModel";
import type { MyWorkCompositeResult } from "../../../convex/composites/myWork";
import type {
  MyWorkView,
  TaskCardShape,
  TaskRowShape,
  ContributionRowShape,
  DecisionItem,
  RefinementRunCard,
} from "../../../../../shared/types/myWorkLane";
import { STATUS_CONFIG, STATUS_FALLBACK } from "../../config/statusConfig";
import { tField } from "../translations";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRIORITY_LABEL: Record<string, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};

// ---------------------------------------------------------------------------
// Local converters (pure — no Date.now, no side effects)
// ---------------------------------------------------------------------------

function toTaskCard(t: Doc<"tasks">): TaskCardShape {
  const cfg = STATUS_CONFIG[t.status];
  return {
    id: t._id,
    title: (t.subject ?? "").slice(0, 60), // exact 60-char cap (test 1)
    contextLine: t.description ?? null,
    priority: PRIORITY_LABEL[t.priority] ?? t.priority,
    ownerAvatarUrl: null,
    statusLabel: cfg?.label ?? tField(t.status),
    statusColor: cfg?.color ?? "var(--color-slate)",
  };
}

function toTaskRow(t: Doc<"tasks">): TaskRowShape {
  return {
    id: t._id,
    title: t.subject ?? "",
    dueDateLabel: t.dueDate
      ? new Date(t.dueDate).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })
      : null,
    dueDateMs: t.dueDate ?? null,
    priority: PRIORITY_LABEL[t.priority] ?? t.priority,
    workspace: t.projectId,
    statusLabel: STATUS_CONFIG[t.status]?.label ?? tField(t.status),
  };
}

function toContributionRow(t: Doc<"tasks">): ContributionRowShape {
  const completedMs = t.completedAt ?? t._creationTime;
  return {
    id: t._id,
    title: t.subject ?? "",
    costDisplay: "\u2014", // em-dash — Phase-2: costSnapshots join
    elapsedRelative: formatElapsed(completedMs, t._creationTime),
    reflectionHref: `#/task/${t._id}/report`,
  };
}

/**
 * Pure elapsed-time formatter. Depends only on inputs — no Date.now().
 */
function formatElapsed(completedMs: number, createdMs: number): string {
  const elapsedMs = Math.max(0, completedMs - createdMs);
  const hours = Math.round(elapsedMs / 3_600_000);
  if (hours < 1) return "< 1 hr";
  if (hours < 24) return `${hours} hr`;
  return `${Math.round(hours / 24)} d`;
}

/**
 * Adapts a raw Doc<"humanTasks"> to the shared DecisionItem shape.
 * The shared DecisionItem (from shared/types/myWorkLane.ts) has a different
 * shape than the deployed transforms/humanTask.ts DecisionItem.
 */
function toDecisionItem(doc: Doc<"humanTasks">): DecisionItem {
  const createdAt = doc.createdAt ?? doc._creationTime;
  const raw = doc as unknown as Record<string, unknown>;
  const payload = raw["payload"];
  const prompt =
    typeof payload === "string"
      ? payload
      : typeof payload === "object" && payload !== null
        ? String(
            (payload as Record<string, unknown>)["message"] ??
              (payload as Record<string, unknown>)["question"] ??
              (payload as Record<string, unknown>)["description"] ??
              "",
          )
        : "";

  const expiresAt = raw["expiresAt"] as number | undefined;
  // isOverdue uses the createdAt + expiresAt relationship, not Date.now()
  // A task is overdue if it has an expiration and that expiration is in the past
  // relative to the creation time anchor. Without Date.now(), we use the
  // expiresAt < createdAt heuristic — but practically, expiresAt is always
  // set to a future absolute timestamp. We check if the field exists and
  // compare against creation time as a pure approximation.
  const isOverdue = expiresAt != null ? expiresAt < createdAt : false;

  return {
    id: String(doc._id),
    title: KIND_TITLE[doc.kind] ?? doc.kind,
    prompt,
    taskType: doc.kind,
    workspaceId: String(raw["sessionId"] ?? ""),
    createdAt: new Date(createdAt).toISOString(),
    isOverdue,
  };
}

/** Human-readable title per HumanTask kind. */
const KIND_TITLE: Record<string, string> = {
  approval: "Approval Required",
  outbound_approval: "Outbound Approval",
  browser_handoff: "Browser Handoff",
  clarification: "Clarification Needed",
  destructive_confirm: "Destructive Confirmation",
};

/**
 * Adapts a raw Doc<"workflowRuns"> to the shared RefinementRunCard shape.
 * The shared RefinementRunCard (from shared/types/myWorkLane.ts) has a
 * different shape than the deployed types/refinementRun.ts RefinementRunCard.
 */
function toRefinementRunCard(doc: Doc<"workflowRuns">): RefinementRunCard {
  const status = doc.status as string;
  const cfg = STATUS_CONFIG[status] ?? STATUS_FALLBACK;
  const raw = doc as unknown as Record<string, unknown>;

  const createdAt = doc.createdAt ?? doc._creationTime;
  const createdAtLabel = new Date(createdAt).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const completedAt = raw["completedAt"] as number | undefined;
  const totalCost = raw["totalCost"] as number | undefined;
  const costDisplay =
    totalCost != null && Number.isFinite(totalCost)
      ? `$${totalCost.toFixed(2)}`
      : "\u2014";

  return {
    runId: String(doc._id),
    workspaceId: String(raw["repositoryId"] ?? ""),
    status,
    statusColor: cfg.color,
    statusBgClass: cfg.bgClass,
    statusLabel: cfg.label,
    workflowName: (raw["workflowName"] as string) ?? "Unnamed workflow",
    createdAt,
    createdAtLabel,
    finishedAt: completedAt ?? null,
    costDisplay,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function transformMyWork(raw: MyWorkCompositeResult): MyWorkView {
  return {
    userFirstName: raw.userFirstName,
    doingNow: raw.doingNow.map(toTaskCard),
    waitingForInput: raw.waitingForInput.map(toDecisionItem),
    reviewRequested: raw.reviewRequested.map(toDecisionItem),
    inFlightRuns: raw.inFlightRuns.map(toRefinementRunCard),
    queue: raw.queue.map(toTaskRow),
    recentContributions: raw.recentContributions.map(toContributionRow),
    hasMoreQueue: raw.hasMoreQueue,
    nextQueueCursor: raw.nextQueueCursor,
    partial: raw.partial,
  };
}
